import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FormInputs from 'apps/web_forms/components/Form/FormInputs';
import MediaFormControls from 'apps/web_forms/components/MediaFormControls';
import { DEVICES_SIZES } from 'apps/web_forms/utils/constants';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import { updateFieldErrorBulk } from 'apps/web_forms/slices/formSlice';

import { FormInputsProvider } from 'apps/web_forms/contexts/FormInputsContext';
import {
  selectFieldErrors,
  selectRepeatGroupList,
  selectIsOpen,
} from 'apps/web_forms/hooks/useGlobalSelector';

import ls from 'local-storage';

const StyledFormElementsContainer = styled.div`
  background-color: white;
  padding: 1.9em;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  .elements-container {
    width: 100%;
    padding-right: 1em;
  }

  @media (min-width: ${DEVICES_SIZES.tablet}) {
    padding-right: 0;

    .elements-container {
      width: ${(props) => (props.isOpen ? '59%' : '95%')};
      padding: 0;
      transition: 0.5s;
    }
  }
`;

StyledFormElementsContainer.displayName = 'StyledFormElementsContainer';

const FormElements = ({
  elements,
  getInput,
  dispatchInitialAnswers,
  formContext,
  formErrors,
}) => {
  const storedFieldErrors = useSelector(selectFieldErrors);
  const repeatGroupList = useSelector(selectRepeatGroupList);
  const isOpen = useSelector(selectIsOpen);

  const loadMode = ls.get('rvMode') ?? false;

  const dispatch = useDispatch();

  // If there are changes in the errors object, this will extract the new error messages
  // then send them to Redux so only fields that have a new error message will be re-rendered
  useEffect(() => {
    const fieldErrors = [];
    const allErrors = { ...storedFieldErrors, ...formErrors };
    Object.keys(allErrors).forEach((fieldIdentifier) => {
      // Filter repeat groups
      if (repeatGroupList[fieldIdentifier]) return;
      const currentErrorMessage = formErrors[fieldIdentifier]?.message;
      if (storedFieldErrors[fieldIdentifier] !== currentErrorMessage) {
        fieldErrors.push({
          identifier: fieldIdentifier,
          errorMessage: currentErrorMessage,
        });
      }
    });
    if (fieldErrors.length > 0) {
      dispatch(updateFieldErrorBulk(fieldErrors));
    }
  }, [
    JSON.stringify(
      Object.entries(formErrors).map(([key, value]) => [key, value.message])
    ),
  ]);

  return (
    <FormInputsProvider
      elements={elements}
      formContext={formContext}
      getValues={formContext.getValues}
      getInput={getInput}
    >
      <StyledFormElementsContainer isOpen={isOpen}>
        <FormInputs
          dispatchInitialAnswers={dispatchInitialAnswers}
          elements={elements}
          setValue={formContext.setValue}
        />
        <MediaFormControls />
      </StyledFormElementsContainer>
    </FormInputsProvider>
  );
};

FormElements.propTypes = {
  elements: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getInput: PropTypes.func.isRequired,
  dispatchInitialAnswers: PropTypes.shape({}),
  formContext: customPropTypes.formContext.isRequired,
  formErrors: PropTypes.shape({}).isRequired,
};

FormElements.defaultProps = {
  dispatchInitialAnswers: undefined,
};

export default FormElements;
